import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, CircularProgress, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const API = process.env.REACT_APP_API_URL || '';

const fieldSx = {
  '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-primario)' },
};
const inputStyle = { style: { color: '#e0e0e0' } };
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };

export default function BusquedaHC() {
  const navigate = useNavigate();
  const [medicos, setMedicos] = useState([]);
  const [q, setQ] = useState('');
  const [dni, setDni] = useState('');
  const [paciente, setPaciente] = useState('');
  const [medicoId, setMedicoId] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/medicos`).then(r => setMedicos(r.data)).catch(() => {});
  }, []);

  async function buscar(e) {
    e?.preventDefault();
    if (!q.trim() && !dni.trim() && !paciente.trim() && !medicoId && !desde && !hasta) {
      setError('Ingresá al menos un criterio de búsqueda.');
      return;
    }
    setError('');
    setBuscando(true);
    try {
      const params = {};
      if (q.trim())       params.q = q.trim();
      if (dni.trim())     params.dni = dni.trim();
      if (paciente.trim()) params.paciente = paciente.trim();
      if (medicoId)       params.medico_id = medicoId;
      if (desde)          params.desde = desde;
      if (hasta)          params.hasta = hasta;
      const r = await axios.get(`${API}/api/evoluciones/buscar`, { params });
      setResultados(r.data);
      setBuscado(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al buscar.');
    } finally {
      setBuscando(false);
    }
  }

  function limpiar() {
    setQ(''); setDni(''); setPaciente(''); setMedicoId(''); setDesde(''); setHasta('');
    setResultados([]); setBuscado(false); setError('');
  }

  function preview(texto) {
    return (texto || '').replace(/<[^>]*>/g, '').substring(0, 100) + ((texto?.length > 100) ? '...' : '');
  }

  function fmtFecha(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 3 }}>
        Búsqueda en Historia Clínica
      </Typography>

      <Box component="form" onSubmit={buscar}
        sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 3, mb: 3,
              border: '1px solid var(--c-primario)' }}>

        {/* Fila 1: paciente */}
        <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
          Paciente
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2, mb: 2.5 }}>
          <TextField
            label="DNI"
            value={dni}
            onChange={e => setDni(e.target.value)}
            placeholder="Ej: 30123456"
            InputLabelProps={labelStyle} inputProps={{ ...inputStyle, maxLength: 20 }} sx={fieldSx}
          />
          <TextField
            label="Nombre / apellido"
            value={paciente}
            onChange={e => setPaciente(e.target.value)}
            placeholder="Ej: García"
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
          />
        </Box>

        {/* Fila 2: contenido clínico + médico */}
        <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
          Contenido clínico
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2.5 }}>
          <TextField
            label="Palabra clave"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Diagnóstico, texto de evolución, medicamento..."
            InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
          />
          <FormControl sx={fieldSx}>
            <InputLabel sx={{ color: 'var(--c-acento)' }}>Médico</InputLabel>
            <Select value={medicoId} label="Médico"
              onChange={e => setMedicoId(e.target.value)}
              sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
              <MenuItem value="">Todos los médicos</MenuItem>
              {medicos.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* Fila 3: fechas + botones */}
        <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
          Período
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '0 0 180px' }}>
            <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', mb: 0.5, fontWeight: 500 }}>Desde</Typography>
            <TextField type="date" value={desde} fullWidth
              onChange={e => setDesde(e.target.value)}
              inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
          </Box>
          <Box sx={{ flex: '0 0 180px' }}>
            <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', mb: 0.5, fontWeight: 500 }}>Hasta</Typography>
            <TextField type="date" value={hasta} fullWidth
              onChange={e => setHasta(e.target.value)}
              inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, pb: 0.1 }}>
            <Button type="submit" variant="contained" startIcon={<SearchIcon />}
              disabled={buscando}
              sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' }, height: 56 }}>
              {buscando ? 'Buscando...' : 'Buscar'}
            </Button>
            {buscado && (
              <Button onClick={limpiar} variant="outlined"
                sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', height: 56 }}>
                Limpiar
              </Button>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>

      {buscando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress sx={{ color: 'var(--c-acento)' }} />
        </Box>
      )}

      {buscado && !buscando && (
        <>
          <Typography sx={{ color: '#b0b0b0', mb: 1.5, fontSize: 13 }}>
            {resultados.length === 0
              ? 'No se encontraron resultados.'
              : `${resultados.length} evolución${resultados.length !== 1 ? 'es' : ''} encontrada${resultados.length !== 1 ? 's' : ''}${resultados.length === 200 ? ' (límite: 200)' : ''}.`}
          </Typography>

          {resultados.length > 0 && (
            <TableContainer component={Paper}
              sx={{ bgcolor: 'var(--c-superficie)', '& .MuiTableCell-root': { color: '#e0e0e0', borderColor: 'var(--c-primario)' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Paciente</TableCell>
                    <TableCell>DNI</TableCell>
                    <TableCell>Médico</TableCell>
                    <TableCell>Diagnóstico</TableCell>
                    <TableCell>Preview</TableCell>
                    <TableCell align="center">HC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultados.map(ev => (
                    <TableRow key={ev.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {fmtFecha(ev.created_at)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {`${ev.paciente_nombre || ''} ${ev.paciente_apellido || ''}`.trim() || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {ev.paciente_dni || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {ev.medico_nombre || ev.cargado_por_nombre || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: '#90caf9' }}>
                        {ev.diagnostico || <span style={{ color: '#666' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: '#b0b0b0', maxWidth: 220 }}>
                        <Typography sx={{ fontSize: 12, color: '#b0b0b0', overflow: 'hidden',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {preview(ev.texto)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                          onClick={() => window.open(`/historial/${ev.paciente_id}`, '_blank')}
                          sx={{ color: 'var(--c-acento)', fontSize: 11, minWidth: 0, px: 1 }}>
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
